import { create } from 'zustand';
import {
  assignSitting as apiAssign,
  executeSitting as apiExecute,
  completeSitting as apiComplete,
  approveSitting as apiApprove,
  rejectSitting as apiReject,
  collectPayment as apiCollectPayment,
  cancelSitting as apiCancel,
  listAssignedSittings as apiListAssigned,
  listActionPendingSittings as apiListActionPending,
  listScheduledSittings as apiListScheduled,
  listSittingsForVisit as apiListForVisit,
} from '../services/api/patientPlanApi';
import {
  addProcedureNote as apiAddProcedureNote,
  listSittingNotes as apiListSittingNotes,
  listVisitNotes as apiListVisitNotes,
} from '../services/api/procedureNoteApi';
import { realtimeMiddleware } from './realtimeMiddleware';
import { REALTIME_EVENTS } from '../services/realtime/RealtimeSyncService';
import eventBus from '../services/realtime/EventBus';
import { coalesce } from '../utils/coalesce';
import { DATA_KEYS } from './dataKeys';

/**
 * Phase 5 — emit a canonical SITTING_* event AFTER each successful
 * local write so `dentalHistoryStore` (chart + summary) refreshes in
 * the same render as `sittingStore` (widget). Mirrors the same recipe
 * used in `RowActionToolbar.emitSittingEvent` (see
 * src/components/procedures/widget/dental/RowActionToolbar.jsx:118).
 *
 * Source field is INTENTIONALLY omitted — the realtime middleware
 * filters `source === 'local'`, which would otherwise hide our local
 * emits from itself. The middleware's re-application of the patch is
 * idempotent (`patchAcrossProjections` keys on sitting_id) so the
 * double-write is a no-op.
 */
function emitSittingLifecycle(eventName, sittingData) {
  try {
    eventBus.emit({
      type: eventName,
      payload: {
        sitting_id: sittingData?.sitting_id || null,
        visit_id: sittingData?.visit_id || null,
        patient_id: sittingData?.patient_id || null,
        status: sittingData?.status || null,
      },
    });
  } catch (_e) { /* handler errors must not break the API success */ }
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Sitting Store — per-sitting state-machine actions + projection caches.
 *
 * Projections:
 *   - `myQueue`         the caller's Procedure Queue (assigned sittings,
 *                       status-prefix filterable)
 *   - `actionPendingSittings`  receptionist action list (all
 *                       awaiting_payment sittings the caller can see)
 *   - `scheduledByDate` { 'YYYY-MM-DD': SittingSummary[] } — calendar
 *   - `byVisit`         { [visit_id]: SittingSummary[] } — inline Procedures
 *                       panel on the visit screen
 *   - `walkinNotifications` — notification card feed for doctors when a
 *                       nurse/receptionist initiates a procedure on their visit
 *
 * Realtime: every SITTING_* event patches whichever projections contain
 * the sitting. The approach is "surgical upsert on the summary shape"
 * rather than refetch, so UIs reflect transitions without a round-trip.
 *
 * Mutating actions (execute, complete, approve, …) call the API, and on
 * success hand the full SittingResponse back to callers AND patch any
 * locally-cached summary shape (where the full response has more fields
 * than a summary; we just overlay the common ones).
 * ═══════════════════════════════════════════════════════════════════════
 */

const SUMMARY_PROJECTION_KEYS = [
  'sitting_id', 'plan_id', 'item_id',
  'status', 'payment_timing', 'timing_mode', 'scheduled_for',
  'requires_approval', 'approver_member_id',
  'visit_id', 'patient_id',
  'procedure_name', 'procedure_category', 'final_cost',
  // Phase 1c — denormalized display names
  'assigned_to_name', 'assigned_by_name',
  // Phase 4.0e — delegator identity. Without this, delegatedByMe's
  // upsert predicate in patchAcrossProjections sees `undefined` and
  // never inserts the row when the viewer reassigns a sitting away.
  'assigned_by_member_id',
  // Phase 1d — visit-doctor axis for delegated-queue pill
  'visit_doctor_member_id',
  // Phase 1e — queue-card identity
  'patient_name', 'queue_number', 'visit_doctor_name',
  // Source (walk-in badge) + status transition timestamps (approval dialog)
  'source',
  // Phase 2.7 + 4.0c — workflow-step provenance + ordering_rule denorm
  // so chips can render strict-blocked lock indicators without a
  // plan-detail fetch.
  'source_plan_id', 'source_step_id', 'step_number', 'is_mandatory',
  'plan_ordering_rule',
  // Creation timestamp — used by ProcedureChip's whenLabel as the
  // fallback when scheduled_for is absent (timing_mode='now' rows).
  // Without this projection key, every such chip rendered "today"
  // regardless of the actual creation date.
  'created_at',
  // Lifecycle stamps used by ProcedureViewPopover's Created / Started /
  // Completed / Paid rows. Backend ships them on every sitting event
  // via build_sitting_event_payload; without these keys the store would
  // drop them on WS upsert and the popover would only see whatever the
  // last REST fetch returned.
  'executed_at',
  'completed_at',
  'payment_cleared_at',
  // Procedures-on-New-Visits — origin trace + cross-visit chain + the
  // VMETA-mirror move breadcrumb. Without these projection keys the
  // upsert-if-missing path would silently drop the chain / origin /
  // moved-to data on any SITTING_* event and the registration dropdown,
  // PatientHistory chain, and "moved to / expired" badges would break.
  'origin_visit_id', 'origin_doctor_member_id', 'origin_doctor_name',
  'origin_suggested_at', 'visit_chain',
  'is_historical', 'moved_to_visit_id', 'moved_to_visit_date', 'moved_at',
  // expiry / standing-order projection (replaces the retired `recurring`)
  'standing', 'expires_at',
];

function sittingToSummaryPatch(sittingOrPayload) {
  const patch = {};
  for (const k of SUMMARY_PROJECTION_KEYS) {
    if (sittingOrPayload[k] !== undefined) patch[k] = sittingOrPayload[k];
  }
  if (sittingOrPayload.assigned_to) {
    patch.assigned_to_member_id = sittingOrPayload.assigned_to.member_id;
    patch.assigned_to_role = sittingOrPayload.assigned_to.role;
  }
  if (sittingOrPayload.assigned_by) {
    patch.assigned_by_member_id = sittingOrPayload.assigned_by.member_id;
  }
  return patch;
}

function upsertById(list, sitting_id, patch) {
  if (!Array.isArray(list)) return list;
  const idx = list.findIndex((s) => s.sitting_id === sitting_id);
  if (idx === -1) return list;
  const next = list.slice();
  next[idx] = { ...next[idx], ...patch };
  return next;
}

/**
 * Insert-if-missing + patch. Used when a WS event carries a full summary
 * projection and the store has reason to believe the sitting should be
 * in the list (e.g. reassigned to me). Returns a new list with the row
 * upserted; existing entries get their fields merged over.
 */
function insertOrUpdateById(list, sitting_id, fullPatch) {
  if (!Array.isArray(list)) return [{ sitting_id, ...fullPatch }];
  const idx = list.findIndex((s) => s.sitting_id === sitting_id);
  if (idx === -1) {
    return [...list, { sitting_id, ...fullPatch }];
  }
  const next = list.slice();
  next[idx] = { ...next[idx], ...fullPatch };
  return next;
}

function removeById(list, sitting_id) {
  if (!Array.isArray(list)) return list;
  return list.filter((s) => s.sitting_id !== sitting_id);
}

/**
 * Phase 1e — Apply a state transition across every projection that
 * holds this sitting. Returns a Zustand-partial patch.
 *
 * If `fullSummary` is provided (from an enriched WS event payload), the
 * myQueue + byVisit projections will UPSERT (insert when missing) when:
 *   - the viewer is the current assignee (myQueue only)
 *   - the viewer is the visit doctor (myQueue only, matches the backend
 *     Phase 1d merge so the senior sees delegated sittings)
 *   - the sitting's visit_id is already a key in byVisit (keeps the
 *     team panel in sync even for rows that arrived mid-session)
 *
 * Without `fullSummary`, behavior falls back to Phase 1d: patch-only,
 * no inserts.
 */
function patchAcrossProjections(
  state, sittingId, fieldPatch,
  { removeFromMyQueue = false, fullSummary = null } = {},
) {
  const patches = {};
  const myId = state.myMemberId;

  // myQueue — patch existing row, upsert when I should see it, or remove
  // on reassign-away.
  const inMyQueue = state.myQueue?.some((s) => s.sitting_id === sittingId);
  if (inMyQueue) {
    patches.myQueue = removeFromMyQueue
      ? removeById(state.myQueue, sittingId)
      : upsertById(state.myQueue, sittingId, fieldPatch);
  } else if (fullSummary) {
    // Not in myQueue today — upsert if I'm the new assignee or visit doctor.
    const assigneeId = fullSummary.assigned_to_member_id;
    const visitDoctorId = fullSummary.visit_doctor_member_id;
    const shouldBeMine =
      myId && (assigneeId === myId || visitDoctorId === myId);
    const terminal = ['completed', 'paid', 'cancelled'].includes(
      fullSummary.status,
    );
    if (shouldBeMine && !terminal) {
      patches.myQueue = insertOrUpdateById(
        state.myQueue || [], sittingId, fullSummary,
      );
    }
  }

  // delegatedByMe (Phase 4.0d) — patch when present; remove on terminal;
  // upsert when I'm the originator and the sitting was just delegated.
  const inDelegated = state.delegatedByMe?.some((s) => s.sitting_id === sittingId);
  if (inDelegated) {
    const terminalNow = ['completed', 'paid', 'cancelled'].includes(
      fieldPatch.status || (fullSummary && fullSummary.status),
    );
    patches.delegatedByMe = terminalNow
      ? removeById(state.delegatedByMe, sittingId)
      : upsertById(state.delegatedByMe, sittingId, fieldPatch);
  } else if (fullSummary && myId) {
    const delegator = fullSummary.assigned_by_member_id;
    const assignee = fullSummary.assigned_to_member_id;
    const terminal = ['completed', 'paid', 'cancelled'].includes(
      fullSummary.status,
    );
    const shouldTrack =
      delegator === myId && assignee && assignee !== myId && !terminal;
    if (shouldTrack) {
      patches.delegatedByMe = insertOrUpdateById(
        state.delegatedByMe || [], sittingId, fullSummary,
      );
    }
  }

  // actionPendingSittings — cleared transitions remove; awaiting_payment upsert.
  if (state.actionPendingSittings?.some((s) => s.sitting_id === sittingId)) {
    const cleared = fieldPatch.status && fieldPatch.status !== 'awaiting_payment';
    patches.actionPendingSittings = cleared
      ? removeById(state.actionPendingSittings, sittingId)
      : upsertById(state.actionPendingSittings, sittingId, fieldPatch);
  } else if (fullSummary && fullSummary.status === 'awaiting_payment') {
    patches.actionPendingSittings = insertOrUpdateById(
      state.actionPendingSittings || [], sittingId, fullSummary,
    );
  }

  // byVisit — patch existing rows; upsert when the visit is known.
  const byVisit = state.byVisit || {};
  const updatedByVisit = {};
  let anyVisitTouched = false;
  let found = false;
  for (const [vid, sittings] of Object.entries(byVisit)) {
    if (sittings.some((s) => s.sitting_id === sittingId)) {
      updatedByVisit[vid] = upsertById(sittings, sittingId, fieldPatch);
      anyVisitTouched = true;
      found = true;
    } else {
      updatedByVisit[vid] = sittings;
    }
  }
  if (!found && fullSummary?.visit_id && byVisit[fullSummary.visit_id]) {
    // Known visit but the sitting hadn't been written yet (e.g. new
    // walk-in added after the visit was loaded). Upsert into that
    // visit's list.
    updatedByVisit[fullSummary.visit_id] = insertOrUpdateById(
      byVisit[fullSummary.visit_id] || [], sittingId, fullSummary,
    );
    anyVisitTouched = true;
  }
  if (anyVisitTouched) patches.byVisit = updatedByVisit;

  return patches;
}

/**
 * Phase 1e — Build a full-summary patch from an enriched WS event
 * payload. Backend `_event_payload` emits every SittingSummary field so
 * this is a direct shape mapping.
 */
function buildFullSummaryFromPayload(payload) {
  if (!payload || !payload.sitting_id) return null;
  const summary = {};
  for (const k of SUMMARY_PROJECTION_KEYS) {
    if (payload[k] !== undefined) summary[k] = payload[k];
  }
  // Unpack assigned_to sub-object for the summary shape used by UI.
  if (payload.assigned_to) {
    summary.assigned_to_member_id = payload.assigned_to.member_id;
    summary.assigned_to_role = payload.assigned_to.role;
  } else if (payload.assignee_member_id) {
    summary.assigned_to_member_id = payload.assignee_member_id;
  }
  return summary;
}


// ── Realtime event handlers ──────────────────────────────────────────

/**
 * Phase 1e — every status-transition handler builds a full-summary
 * patch from the enriched WS payload, so projections that didn't
 * previously contain the sitting can upsert it.
 */
function statusTransitionHandler(defaultStatus) {
  return (state, event) => {
    const payload = event.payload || {};
    const { sitting_id, status = defaultStatus } = payload;
    if (!sitting_id) return {};
    const fullSummary = buildFullSummaryFromPayload({ ...payload, status });
    return patchAcrossProjections(
      state, sitting_id,
      { ...(fullSummary || {}), status },
      { fullSummary },
    );
  };
}

/**
 * Phase 3 — `SITTING_READY` is the "your turn now" promotion event for
 * non-first strict steps. We patch the row's status into `ready` (so the
 * chip flips into actionable state without a refetch) AND fire the
 * "your turn" notification: toast + chime when the assignee is the
 * current viewer. Falls back to silent state-update for non-assignees so
 * the doctor's plan-timeline view also refreshes.
 */
function readyHandler(state, event) {
  const payload = event.payload || {};
  const { sitting_id } = payload;
  if (!sitting_id) return {};

  const fullSummary = buildFullSummaryFromPayload({ ...payload, status: 'ready' });
  const patches = patchAcrossProjections(
    state, sitting_id,
    { ...(fullSummary || {}), status: 'ready' },
    { fullSummary },
  );

  // Notify the assignee — only fire on remote events (skip local echoes
  // from the user's own action) and only when the viewer is the row's
  // assignee. Best-effort: import lazily so test environments without
  // toast/sound infra don't break.
  const isRemote = event?.source === 'remote';
  const assigneeId = payload.assigned_to_member_id
    || (payload.assigned_to && payload.assigned_to.member_id)
    || null;
  const isMine = state.myMemberId && assigneeId && assigneeId === state.myMemberId;
  if (isRemote && isMine) {
    notifyAssigneeReady(payload);
  }
  return patches;
}

/**
 * Phase 3 — fire "your turn" toast + chime + browser notification.
 * Best-effort; never throws into the WS pipeline.
 */
function notifyAssigneeReady(payload) {
  try {
    const procedure = payload.procedure_name || 'next procedure';
    const patient = payload.patient_name ? ` for ${payload.patient_name}` : '';
    const message = `\u25B6 Your next procedure is ready: ${procedure}${patient}.`;

    // Toast: prefer a global emitter if available; fall back to console.
    if (typeof window !== 'undefined') {
      if (typeof window.toast === 'function') {
        window.toast({ kind: 'info', message, durationMs: 8000 });
      } else if (window.dispatchEvent) {
        try {
          window.dispatchEvent(new CustomEvent('app:toast', {
            detail: { kind: 'info', message, durationMs: 8000 },
          }));
        } catch (_e) { /* ignore */ }
      }

      // Chime — short audio cue. Reuse an existing /sounds/your-turn.mp3
      // asset if present; silent if missing.
      try {
        const audio = new Audio('/sounds/your-turn.mp3');
        audio.volume = 0.4;
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => { /* autoplay blocked, ignore */ });
        }
      } catch (_e) { /* ignore */ }

      // Browser notification when the tab is backgrounded.
      try {
        if ('Notification' in window
          && Notification.permission === 'granted'
          && document.visibilityState !== 'visible') {
          new Notification('Procedure ready', { body: message });
        }
      } catch (_e) { /* ignore */ }
    }
  } catch (_e) { /* ignore */ }
}

/**
 * K5 — chart opens, or any other surface wants every store to re-confirm
 * freshness. Trigger a `fetchForVisit(visit_id)` on this store so the
 * Procedures widget below the chart syncs without waiting for the next
 * WS event. No-op when visit_id is missing.
 *
 * The middleware expects each handler to return a state patch, but
 * `fetchForVisit` mutates state internally via `set` (it's a thunk).
 * We return `{}` (no immediate patch) and let the thunk do the work.
 */
function toothChartRefreshHandler(_state, event) {
  const visitId = event?.payload?.visit_id;
  if (visitId) {
    // Lazy-grab the store API to avoid the chicken-and-egg with the
    // middleware initialising the store.
    queueMicrotask(() => {
      try {
        const store = require('./sittingStore').default;
        // force: realtime echo needs post-change truth — must not piggyback
        // on a coalesced in-flight request that predates the change.
        store.getState().fetchForVisit?.(visitId, { force: true });
      } catch (_e) { /* SSR / HMR */ }
    });
  }
  return {};
}

// Per-sitting `SITTING_PAYMENT_CLEARED` events race the VMETA mirror sync
// (and the preceding `SITTING_AWAITING_PAYMENT` event ordering during
// chunked bundle collection), so the doctor's right-edge Procedures pill
// can be left without the chip even though the sitting is now
// payment_cleared. The bundled-payment broadcast is the single
// deterministic end-of-flow event — use it to reconcile the owner-axis
// projections.
//
// CRITICAL: this reconcile is DEBOUNCED, not immediate. The assignee index
// is a DynamoDB GSI, which is ALWAYS eventually-consistent — an immediate
// refetch races the just-written payment and can read back the pre-collect
// `awaiting_payment` row, clobbering the correct write-time status the
// per-sitting `SITTING_PAYMENT_CLEARED` handlers already patched in
// (symptom: chip stays "Awaiting payment" until the widget is reopened).
// Debouncing lets GSI propagation settle so the reconcile reads fresh and
// never clobbers. The per-sitting patches give the instant update; this is
// the backstop for the chip-less race only.
const BUNDLED_RECONCILE_DELAY_MS = 1500;
let bundledReconcileTimer = null;
function bundledPaymentRefreshHandler(_state, _event) {
  if (bundledReconcileTimer) clearTimeout(bundledReconcileTimer);
  bundledReconcileTimer = setTimeout(() => {
    bundledReconcileTimer = null;
    try {
      const store = require('./sittingStore').default;
      const api = store.getState();
      api.fetchMyQueue?.({}, { force: true });
      api.fetchDelegatedByMe?.({ force: true });
    } catch (_e) { /* SSR / HMR */ }
  }, BUNDLED_RECONCILE_DELAY_MS);
  return {};
}

// VISIT_STATUS_CHANGED carries the parent visit's new status, but the
// sitting rows in `myQueue` have `visit_status` denormed at fetch time
// — they go stale when the parent visit transitions. Chip-render code
// already prefers the live visit row from `visitStore` (Bug A fix), but
// downstream consumers may still read `sitting.visit_status`. Refetch
// `myQueue` to keep the denorm honest. Only fires when the changed
// visit actually owns a cached sitting — keeps cost proportional.
function visitStatusChangedRefreshHandler(state, event) {
  const visitId = event.payload?.visitId || event.payload?.visit_id;
  if (!visitId) return {};
  const affected = (state.myQueue || []).some((s) => s.visit_id === visitId);
  if (!affected) return {};
  queueMicrotask(() => {
    try {
      const store = require('./sittingStore').default;
      const api = store.getState();
      // force: post-change reconcile must hit the server, not piggyback stale.
      api.fetchMyQueue?.({}, { force: true });
    } catch (_e) { /* SSR / HMR */ }
  });
  return {};
}

const sittingEventHandlers = {
  [REALTIME_EVENTS.SITTING_AWAITING_PAYMENT]: statusTransitionHandler('awaiting_payment'),
  [REALTIME_EVENTS.SITTING_PAYMENT_CLEARED]: statusTransitionHandler('payment_cleared'),
  [REALTIME_EVENTS.VISIT_BUNDLED_PAYMENT_COLLECTED]: bundledPaymentRefreshHandler,
  // Resume Visit feature — keep denormed sitting.visit_status fresh.
  [REALTIME_EVENTS.VISIT_STATUS_CHANGED]: visitStatusChangedRefreshHandler,
  // Phase 3 — strict-plan one-by-one promotion. Held is a silent status
  // change (the chip moves to muted in the doctor's timeline). Ready
  // fires the "your turn" notification.
  [REALTIME_EVENTS.SITTING_HELD]: statusTransitionHandler('held'),
  [REALTIME_EVENTS.SITTING_READY]: readyHandler,
  [REALTIME_EVENTS.SITTING_SCHEDULED]: statusTransitionHandler('scheduled'),
  [REALTIME_EVENTS.SITTING_EXECUTION_STARTED]: statusTransitionHandler('in_progress'),
  [REALTIME_EVENTS.SITTING_COMPLETED]: statusTransitionHandler('completed'),
  [REALTIME_EVENTS.SITTING_PENDING_APPROVAL]: statusTransitionHandler('pending_approval'),
  [REALTIME_EVENTS.SITTING_APPROVED]: statusTransitionHandler('completed'),
  [REALTIME_EVENTS.SITTING_REJECTED]: statusTransitionHandler('in_progress'),

  // K5 — local "tooth chart opened / chart action committed" signal.
  // Refetches per-visit sittings so the Procedures widget stays in
  // sync with the chart's actions even if WebSocket is asleep.
  TOOTH_CHART_REFRESH: toothChartRefreshHandler,

  [REALTIME_EVENTS.SITTING_REASSIGNED]: (state, event) => {
    // Phase 1d + 1e — cross-assignee move.
    //   myQueue:
    //     - If I'm the new assignee OR the visit doctor → upsert (may be
    //       new to my queue).
    //     - If I'm neither → remove from myQueue (if present).
    //   byVisit: always patch the new assignee identity so the team
    //     panel flips live.
    const payload = event.payload || {};
    const { sitting_id, to_member_id } = payload;
    if (!sitting_id) return {};

    const assigneePatch = {};
    if (to_member_id) assigneePatch.assigned_to_member_id = to_member_id;
    if (payload.to_role) assigneePatch.assigned_to_role = payload.to_role;
    if (payload.to_name != null) assigneePatch.assigned_to_name = payload.to_name;
    if (payload.changed_by_name != null) {
      assigneePatch.assigned_by_name = payload.changed_by_name;
    }
    if (payload.changed_by_member_id != null) {
      // Phase 4.0e — delegator identity. Without this the delegatedByMe
      // upsert predicate in patchAcrossProjections has no way to know
      // who originated the reassign.
      assigneePatch.assigned_by_member_id = payload.changed_by_member_id;
    }

    const myId = state.myMemberId;
    const inMyQueue = state.myQueue?.some((s) => s.sitting_id === sitting_id);
    let keepInMyQueue = false;
    if (inMyQueue) {
      const row = state.myQueue.find((s) => s.sitting_id === sitting_id);
      const isNewAssignee = myId && to_member_id && to_member_id === myId;
      const isVisitDoctor = myId && row?.visit_doctor_member_id === myId;
      keepInMyQueue = isNewAssignee || isVisitDoctor;
    }

    // Build fullSummary unconditionally — it's needed by
    // patchAcrossProjections for BOTH myQueue (new assignee) and
    // delegatedByMe (new delegator) inserts. We seed it with whatever
    // the existing row in myQueue / byVisit already has so the summary
    // doesn't drop fields that the thin WS payload omits.
    const existingRow = state.myQueue?.find((s) => s.sitting_id === sitting_id)
      || Object.values(state.byVisit || {})
        .flat()
        .find((s) => s.sitting_id === sitting_id)
      || null;
    const fullSummary = buildFullSummaryFromPayload({
      ...(existingRow || {}),
      ...payload,
      assigned_to: to_member_id
        ? { member_id: to_member_id, role: payload.to_role || '' }
        : undefined,
      assigned_to_name: payload.to_name,
      assigned_by_member_id: payload.changed_by_member_id,
      assigned_by_name: payload.changed_by_name,
    });

    return patchAcrossProjections(
      state, sitting_id, assigneePatch,
      {
        removeFromMyQueue: inMyQueue && !keepInMyQueue,
        fullSummary,
      },
    );
  },

  [REALTIME_EVENTS.SITTING_VISIT_MOVED]: (state, event) => {
    // Procedures-on-New-Visits — the sitting's live ownership moved to a new
    // visit at re-registration. Re-key byVisit: drop it from the OLD visit's
    // list (it's now a read-only historical breadcrumb there) and upsert it
    // onto the NEW visit (only if that visit is already loaded; otherwise a
    // later REST fetch picks it up). Status/assignee are unchanged, so
    // myQueue / delegatedByMe rows just get their visit_id + denorm refreshed.
    const payload = event.payload || {};
    const sittingId = payload.sitting_id;
    const newVisitId = payload.visit_id;
    if (!sittingId || !newVisitId) return {};

    const existingRow = state.myQueue?.find((s) => s.sitting_id === sittingId)
      || Object.values(state.byVisit || {}).flat().find((s) => s.sitting_id === sittingId)
      || null;
    const fullSummary = buildFullSummaryFromPayload({ ...(existingRow || {}), ...payload });

    const patches = {};
    const byVisit = state.byVisit || {};
    const nextByVisit = {};
    let touched = false;
    for (const [vid, sittings] of Object.entries(byVisit)) {
      let next = sittings;
      const has = Array.isArray(sittings) && sittings.some((s) => s.sitting_id === sittingId);
      if (vid === newVisitId) {
        next = insertOrUpdateById(sittings, sittingId, fullSummary);
        touched = true;
      } else if (has) {
        next = removeById(sittings, sittingId);
        touched = true;
      }
      nextByVisit[vid] = next;
    }
    if (touched) patches.byVisit = nextByVisit;

    if (state.myQueue?.some((s) => s.sitting_id === sittingId)) {
      patches.myQueue = upsertById(state.myQueue, sittingId, fullSummary);
    }
    if (state.delegatedByMe?.some((s) => s.sitting_id === sittingId)) {
      patches.delegatedByMe = upsertById(state.delegatedByMe, sittingId, fullSummary);
    }
    return patches;
  },

  [REALTIME_EVENTS.SITTING_CANCELLED]: (state, event) => {
    const { sitting_id } = event.payload || {};
    if (!sitting_id) return {};
    return patchAcrossProjections(state, sitting_id, { status: 'cancelled' }, {
      removeFromMyQueue: true,
    });
  },

  [REALTIME_EVENTS.SITTING_WALKIN_NOTIFICATION]: (state, event) => {
    // Append to the notification feed; dedupe by sitting_id.
    const p = event.payload;
    if (!p?.sitting_id) return {};
    const existing = state.walkinNotifications || [];
    if (existing.some((n) => n.sitting_id === p.sitting_id)) return {};
    return { walkinNotifications: [p, ...existing].slice(0, 50) };
  },

  // Phase 4.0i — procedure-note added.
  [REALTIME_EVENTS.SITTING_NOTE_ADDED]: (state, event) => {
    const note = event.payload;
    if (!note?.note_id || !note?.sitting_id) return {};
    const map = state.notesBySitting || {};
    const list = map[note.sitting_id] || [];
    if (list.some((n) => n.note_id === note.note_id)) return {}; // idempotent
    // Maintain ascending created_at order so the chat dialog can
    // render directly.
    const next = [...list, note].sort((a, b) =>
      String(a.created_at).localeCompare(String(b.created_at)),
    );
    return {
      notesBySitting: {
        ...map,
        [note.sitting_id]: next,
      },
    };
  },

  // NOTE: the WS-(re)connect self-heal is wired via a DIRECT eventBus
  // listener at the bottom of this module — NOT here. `REALTIME_EVENTS.CONNECTED`
  // is emitted with source:'local', and realtimeMiddleware skips every local
  // event, so a handler in this map would never fire (it was dead code).
};

let _reconnectRefetchTimer = null;
function _scheduleReconnectRefetch() {
  if (_reconnectRefetchTimer) clearTimeout(_reconnectRefetchTimer);
  _reconnectRefetchTimer = setTimeout(() => {
    _reconnectRefetchTimer = null;
    const api = useSittingStore.getState();
    // force: reconnect self-heal is an explicit server-truth sync — bypass
    // coalescing so it can't piggyback on a stale in-flight request.
    if (api?.fetchMyQueue) api.fetchMyQueue({}, { force: true }).catch(() => {});
    if (api?.fetchActionPendingSittings) api.fetchActionPendingSittings({}, { force: true }).catch(() => {});
    if (api?.fetchDelegatedByMe) api.fetchDelegatedByMe({ force: true }).catch(() => {});
  }, 500);
}


const useSittingStore = create(
  realtimeMiddleware(
    (set, get) => ({
      // ── Identity (set by the app shell when the user logs in) ────────
      myMemberId: null,

      // ── Projections ──────────────────────────────────────────────────
      myQueue: [],
      actionPendingSittings: [],
      // Phase 4.0d — sittings the viewer originated (assigned_by ===
      // myMemberId) and that are still in flight. Senior doctors track
      // delegated work here across visits; sticky until terminal.
      delegatedByMe: [],
      scheduledByDate: {},   // { 'YYYY-MM-DD': SittingSummary[] }
      byVisit: {},           // { [visit_id]: SittingSummary[] }
      walkinNotifications: [],
      // Phase 4.0i — procedure-note conversation threads. Keyed by
      // sitting_id. Hydrated on demand via fetchSittingNotes; updated
      // live via the SITTING_NOTE_ADDED WS handler.
      notesBySitting: {},    // { [sitting_id]: ProcedureNote[] }

      isLoading: false,
      error: null,

      // ── Identity setter ──────────────────────────────────────────────
      setMyMemberId: (memberId) => set({ myMemberId: memberId }),

      // ── Fetches ──────────────────────────────────────────────────────
      // Wrapped in per-slice in-flight coalescing so the dashboard mount
      // storm (many widgets fetching at once) collapses to one request and
      // piggybacks on the dashboard-summary hydration. { force: true }
      // bypasses coalescing (hydrator fallback path). Param-scoped calls
      // (status_prefix / timing) get their own key so they never collide
      // with the dashboard's unparametrised fetch.
      fetchMyQueue: async ({ status_prefix } = {}, opts = {}) => {
        const key = DATA_KEYS.myQueue + (status_prefix ? `:${status_prefix}` : '');
        const doFetch = async () => {
          set({ isLoading: true, error: null });
          const result = await apiListAssigned({ status_prefix });
          if (result.success) {
            set({ myQueue: result.data?.sittings || [], isLoading: false });
          } else {
            set({ error: result.error, isLoading: false });
          }
          return result;
        };
        return opts.force ? doFetch() : coalesce(key, doFetch);
      },

      /**
       * Phase 4.0e — sittings the caller delegated to someone else.
       *
       * Backend lens filter (`/sittings/assigned?lens=delegated_by_me`)
       * returns sittings where `assigned_by.member_id == me &&
       * assigned_to.member_id != me && status not terminal`. Replaces
       * the unreliable client-side partition we used to derive from
       * `fetchMyQueue` in 4.0d.
       */
      fetchDelegatedByMe: async (opts = {}) => {
        const doFetch = async () => {
          const result = await apiListAssigned({ lens: 'delegated_by_me' });
          if (result.success) {
            set({ delegatedByMe: result.data?.sittings || [] });
          } else {
            set({ error: result.error });
          }
          return result;
        };
        return opts.force ? doFetch() : coalesce(DATA_KEYS.delegated, doFetch);
      },

      fetchActionPendingSittings: async ({ timing } = {}, opts = {}) => {
        const key = DATA_KEYS.actionPending + (timing ? `:${timing}` : '');
        const doFetch = async () => {
          set({ isLoading: true, error: null });
          const result = await apiListActionPending({ timing });
          if (result.success) {
            set({ actionPendingSittings: result.data?.sittings || [], isLoading: false });
          } else {
            set({ error: result.error, isLoading: false });
          }
          return result;
        };
        return opts.force ? doFetch() : coalesce(key, doFetch);
      },

      fetchScheduledForDate: async (date) => {
        set({ isLoading: true, error: null });
        const result = await apiListScheduled({ date });
        if (result.success) {
          set((s) => ({
            scheduledByDate: { ...s.scheduledByDate, [date]: result.data?.sittings || [] },
            isLoading: false,
          }));
        } else {
          set({ error: result.error, isLoading: false });
        }
        return result;
      },

      // Coalesced per visit: the visit-detail screen mounts ~8 components
      // (VisitProceduresPanel, BundledPaymentSheet, VisitContextHeader,
      // ExpandedProcedureCanvas, …) that each call fetchForVisit(sameId) on
      // mount — without this they each hit the network (we measured 6+/sec
      // for one visit, tripping the 120/min limit). { force: true } bypasses
      // (e.g. a realtime echo that needs fresh truth).
      fetchForVisit: async (visitId, opts = {}) => {
        const doFetch = async () => {
          set({ isLoading: true, error: null });
          const result = await apiListForVisit(visitId);
          if (result.success) {
            set((s) => ({
              byVisit: { ...s.byVisit, [visitId]: result.data?.sittings || [] },
              isLoading: false,
            }));
          } else {
            set({ error: result.error, isLoading: false });
          }
          return result;
        };
        return opts.force
          ? doFetch()
          : coalesce(`${DATA_KEYS.forVisit}:${visitId}`, doFetch);
      },

      // ── Phase 4.0i — procedure-note thread ────────────────────────────
      //
      // Notes are loaded on demand when the dialog mounts. The WS handler
      // (SITTING_NOTE_ADDED) keeps the cache live afterwards across every
      // open client.
      fetchSittingNotes: async (planId, sittingId) => {
        const result = await apiListSittingNotes(planId, sittingId);
        if (result.success) {
          set((s) => ({
            notesBySitting: {
              ...s.notesBySitting,
              [sittingId]: result.data?.notes || [],
            },
          }));
        } else {
          set({ error: result.error });
        }
        return result;
      },

      // Eager visit-level fetch: one GSI9 Query returns every procedure
      // note across every sitting on the visit. We group by sitting_id
      // and replace each affected entry in `notesBySitting` so the
      // inline preview + dialog read the same cache the WS handler
      // patches live.
      fetchVisitNotes: async (visitId) => {
        const result = await apiListVisitNotes(visitId);
        if (result.success) {
          const all = result.data?.notes || [];
          const grouped = {};
          for (const n of all) {
            const sid = n.sitting_id;
            if (!sid) continue;
            if (!grouped[sid]) grouped[sid] = [];
            grouped[sid].push(n);
          }
          for (const sid of Object.keys(grouped)) {
            grouped[sid].sort((a, b) =>
              String(a.created_at).localeCompare(String(b.created_at)),
            );
          }
          set((s) => ({
            notesBySitting: {
              ...(s.notesBySitting || {}),
              ...grouped,
            },
          }));
        } else {
          set({ error: result.error });
        }
        return result;
      },

      addSittingNote: async (planId, sittingId, body) => {
        set({ error: null });
        const result = await apiAddProcedureNote(planId, sittingId, { body });
        if (result.success && result.data) {
          // Optimistic-friendly: append immediately. The WS event
          // handler dedupes by note_id so a second arrival is a no-op.
          set((s) => {
            const existing = s.notesBySitting?.[sittingId] || [];
            if (existing.some((n) => n.note_id === result.data.note_id)) {
              return {};
            }
            const next = [...existing, result.data].sort((a, b) =>
              String(a.created_at).localeCompare(String(b.created_at)),
            );
            return {
              notesBySitting: {
                ...(s.notesBySitting || {}),
                [sittingId]: next,
              },
            };
          });
        } else if (!result.success) {
          set({ error: result.error });
        }
        return result;
      },

      // ── State machine actions ────────────────────────────────────────
      //
      // Every mutator clears `error` on entry, patches projections on
      // success, and sets `error` on failure so the UI banner surfaces
      // what went wrong instead of leaving the user staring at a card
      // that won't advance. The returned {success, error} shape is what
      // dialog wrappers inspect to decide whether to stay open.

      assignSitting: async (planId, sittingId, body) => {
        set({ error: null });
        const result = await apiAssign(planId, sittingId, body);
        if (result.success) {
          const patch = sittingToSummaryPatch(result.data);
          // Pass fullSummary so patchAcrossProjections can insert into
          // delegatedByMe when the caller just delegated this sitting
          // away. Without it the optimistic update only patches existing
          // rows and the delegator's "Delegated" tab stays empty until
          // the WS event arrives (Phase 4.0e bug).
          set((s) => patchAcrossProjections(s, sittingId, patch, {
            fullSummary: patch,
          }));
        } else {
          set({ error: result.error });
        }
        return result;
      },

      executeSitting: async (planId, sittingId) => {
        set({ error: null });
        const result = await apiExecute(planId, sittingId);
        if (result.success) {
          const patch = sittingToSummaryPatch(result.data);
          set((s) => patchAcrossProjections(s, sittingId, patch));
          emitSittingLifecycle(REALTIME_EVENTS.SITTING_EXECUTION_STARTED, result.data);
        } else {
          set({ error: result.error });
        }
        return result;
      },

      completeSitting: async (planId, sittingId, body = {}) => {
        set({ error: null });
        const result = await apiComplete(planId, sittingId, body);
        if (result.success) {
          const patch = sittingToSummaryPatch(result.data);
          set((s) => patchAcrossProjections(s, sittingId, patch));
          emitSittingLifecycle(REALTIME_EVENTS.SITTING_COMPLETED, result.data);
        } else {
          set({ error: result.error });
        }
        return result;
      },

      approveSitting: async (planId, sittingId) => {
        set({ error: null });
        const result = await apiApprove(planId, sittingId);
        if (result.success) {
          const patch = sittingToSummaryPatch(result.data);
          set((s) => patchAcrossProjections(s, sittingId, patch));
          emitSittingLifecycle(REALTIME_EVENTS.SITTING_APPROVED, result.data);
        } else {
          set({ error: result.error });
        }
        return result;
      },

      rejectSitting: async (planId, sittingId, { rejection_reason }) => {
        set({ error: null });
        const result = await apiReject(planId, sittingId, { rejection_reason });
        if (result.success) {
          const patch = sittingToSummaryPatch(result.data);
          set((s) => patchAcrossProjections(s, sittingId, patch));
          emitSittingLifecycle(REALTIME_EVENTS.SITTING_REJECTED, result.data);
        } else {
          set({ error: result.error });
        }
        return result;
      },

      collectPayment: async (planId, sittingId, body) => {
        set({ error: null });
        const result = await apiCollectPayment(planId, sittingId, body);
        if (result.success) {
          const sitting = result.data?.sitting || {};
          const patch = sittingToSummaryPatch(sitting);
          set((s) => patchAcrossProjections(s, sittingId, patch));
          emitSittingLifecycle(REALTIME_EVENTS.SITTING_PAYMENT_CLEARED, sitting);
        } else {
          set({ error: result.error });
        }
        return result;
      },

      cancelSitting: async (planId, sittingId, { reason }) => {
        set({ error: null });
        const result = await apiCancel(planId, sittingId, { reason });
        if (result.success) {
          set((s) => patchAcrossProjections(
            s, sittingId, { status: 'cancelled' }, { removeFromMyQueue: true },
          ));
          // cancelSitting API doesn't return a full sitting payload; pass
          // what we know (sitting_id + status) so the event listener can
          // still scope its refresh to the right visit if available.
          emitSittingLifecycle(REALTIME_EVENTS.SITTING_CANCELLED, {
            sitting_id: sittingId,
            status: 'cancelled',
            // visit_id / patient_id absent → dentalHistoryStore refreshes
            // every cached patient (cheap; coalesced + debounced 250ms).
          });
        } else {
          set({ error: result.error });
        }
        return result;
      },

      // ── Walk-in notification feed management ─────────────────────────
      dismissWalkinNotification: (sittingId) =>
        set((s) => ({
          walkinNotifications: (s.walkinNotifications || []).filter(
            (n) => n.sitting_id !== sittingId,
          ),
        })),

      clearError: () => set({ error: null }),
    }),
    {
      storeName: 'sitting',
      eventHandlers: sittingEventHandlers,
    },
  ),
);

export default useSittingStore;

// ── WS self-heal (registered DIRECTLY on the bus, not via middleware) ──
// `REALTIME_EVENTS.CONNECTED` is emitted source:'local', and
// realtimeMiddleware skips every local event — so this must NOT live in the
// handler map (it would be dead code). Subscribing directly here makes the
// reconnect refetch actually fire.
//   1. On every WS (re)connect → refetch the sitting projections, recovering
//      any events missed during the dropout.
//   2. A periodic safety refetch (focused tab, post-login only) heals a stable
//      connection that simply never received an event — so counts/lists never
//      sit stale waiting for a manual refresh.
const _SELF_HEAL_INTERVAL_MS = 60_000;
eventBus.on(REALTIME_EVENTS.CONNECTED, _scheduleReconnectRefetch);
if (typeof setInterval === 'function') {
  setInterval(() => {
    const tabVisible = typeof document === 'undefined'
      || document.visibilityState === 'visible';
    if (tabVisible && useSittingStore.getState().myMemberId) {
      _scheduleReconnectRefetch();
    }
  }, _SELF_HEAL_INTERVAL_MS);
}

// Exported for tests — lets us invoke the realtime handlers against a
// synthesized state without setting up the WebSocket + EventBus.
export { sittingEventHandlers };
